import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AlertPills from "../Utils/AlertPills";
import {
   createRecommendedVideo,
   deleteRecommendedVideo,
   getRecommendedVideos,
   updateRecommendedVideo,
} from "../../api/recommendedVideosService";

const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;

function parseYoutubeId(input) {
   const raw = String(input || "").trim();
   if (!raw) return "";
   if (YOUTUBE_ID_RE.test(raw)) return raw;

   let normalized = raw;
   if (!/^https?:\/\//i.test(normalized)) normalized = `https://${normalized}`;

   let url;
   try {
      url = new URL(normalized);
   } catch {
      return "";
   }

   const host = String(url.hostname || "")
      .replace(/^www\./i, "")
      .toLowerCase();

   if (host === "youtu.be") {
      const id = String(url.pathname || "")
         .split("/")
         .filter(Boolean)[0];
      return YOUTUBE_ID_RE.test(id) ? id : "";
   }

   if (
      host === "youtube.com" ||
      host.endsWith(".youtube.com") ||
      host === "youtube-nocookie.com" ||
      host.endsWith(".youtube-nocookie.com")
   ) {
      const fromQuery = String(url.searchParams.get("v") || "").trim();
      if (YOUTUBE_ID_RE.test(fromQuery)) return fromQuery;

      const parts = String(url.pathname || "")
         .split("/")
         .filter(Boolean);

      const markerIndex = parts.findIndex((segment) => {
         const value = String(segment || "").toLowerCase();
         return (
            value === "embed" ||
            value === "shorts" ||
            value === "live" ||
            value === "v"
         );
      });

      if (markerIndex >= 0) {
         const maybeId = String(parts[markerIndex + 1] || "").trim();
         if (YOUTUBE_ID_RE.test(maybeId)) return maybeId;
      }
   }

   return "";
}

// Mapează un videoclip din API la forma folosită în popup.
function mapApiVideo(raw, index = 0) {
   if (!raw || typeof raw !== "object") return null;

   const sourceUrl = raw.youtubeUrl || raw.url || raw.link || raw.youtube || "";
   const youtubeId = parseYoutubeId(sourceUrl);
   if (!youtubeId) return null;

   const id = raw.id ?? raw._id ?? `video-${youtubeId}-${index + 1}`;
   const fallbackName = `Material video ${String(index + 1).padStart(2, "0")}`;
   const sendCount =
      raw.sendCount ??
      raw.sentCount ??
      raw.sendsCount ??
      raw._count?.sends ??
      raw._count?.videoSends ??
      null;

   return {
      id,
      youtubeId,
      title: String(raw.title || raw.name || fallbackName).trim() || fallbackName,
      url: String(sourceUrl || `https://www.youtube.com/watch?v=${youtubeId}`),
      sendCount: sendCount != null ? Number(sendCount) : null,
   };
}

function watchUrl(youtubeId) {
   return `https://www.youtube.com/watch?v=${youtubeId}`;
}

export default function StudentVideoLibraryPopup() {
   const [videos, setVideos] = useState([]);
   const [loading, setLoading] = useState(true);
   const [view, setView] = useState("list"); // list | add | edit
   const [searchText, setSearchText] = useState("");
   const [selectedVideoId, setSelectedVideoId] = useState("");

   const [newLink, setNewLink] = useState("");
   const [newTitle, setNewTitle] = useState("");
   const [addBusy, setAddBusy] = useState(false);

   const [editLink, setEditLink] = useState("");
   const [editTitle, setEditTitle] = useState("");
   const [editBusy, setEditBusy] = useState(false);
   const [deleteBusy, setDeleteBusy] = useState(false);

   const [alerts, setAlerts] = useState([]);

   const aliveRef = useRef(true);
   useEffect(() => {
      aliveRef.current = true;
      return () => {
         aliveRef.current = false;
      };
   }, []);

   const pushAlert = (type, text) =>
      setAlerts((prev) => [
         ...prev,
         { id: Date.now() + Math.random(), type, text },
      ]);

   const dismissLastAlert = () => setAlerts((prev) => prev.slice(0, -1));

   useEffect(() => {
      if (!alerts.length) return;
      const tmo = setTimeout(dismissLastAlert, 4000);
      return () => clearTimeout(tmo);
   }, [alerts]);

   const refresh = useCallback(async () => {
      setLoading(true);
      try {
         const res = await getRecommendedVideos({ page: 1, limit: 50 });
         if (!aliveRef.current) return;
         const mapped = (res.items || [])
            .map((item, index) => mapApiVideo(item, index))
            .filter(Boolean);
         setVideos(mapped);
      } catch (e) {
         if (aliveRef.current) {
            pushAlert(
               "error",
               e?.message || "Nu s-au putut încărca videoclipurile.",
            );
         }
      } finally {
         if (aliveRef.current) setLoading(false);
      }
   }, []);

   useEffect(() => {
      refresh();
   }, [refresh]);

   const filteredVideos = useMemo(() => {
      const query = String(searchText || "")
         .trim()
         .toLowerCase();

      if (!query) return videos;

      return videos.filter((video) => {
         const text =
            `${video.title || ""} ${video.url || ""} ${video.youtubeId || ""}`.toLowerCase();
         return text.includes(query);
      });
   }, [videos, searchText]);

   const selectedVideo = useMemo(
      () =>
         videos.find((video) => String(video.id) === String(selectedVideoId)) ||
         null,
      [videos, selectedVideoId],
   );

   useEffect(() => {
      if (!selectedVideo) {
         setEditLink("");
         setEditTitle("");
         return;
      }

      setEditLink(selectedVideo.url || "");
      setEditTitle(selectedVideo.title || "");
   }, [selectedVideo]);

   const handleOpenEdit = (videoId) => {
      setSelectedVideoId(String(videoId));
      setView("edit");
      setAlerts([]);
   };

   const handleOpenAdd = () => {
      setView("add");
   };

   const handleOpenList = () => {
      setView("list");
   };

   const handleOpenVideo = (video) => {
      const href = String(video?.url || "").trim();
      if (!href || typeof window === "undefined") return;
      window.open(href, "_blank", "noopener,noreferrer");
   };

   const handleAddVideo = async (event) => {
      event.preventDefault();
      if (addBusy) return;

      const youtubeId = parseYoutubeId(newLink);
      if (!youtubeId) {
         pushAlert(
            "error",
            "Link YouTube invalid. Folosește watch?v=..., youtu.be/... sau ID video.",
         );
         return;
      }

      if (videos.some((video) => video.youtubeId === youtubeId)) {
         pushAlert("error", "Acest videoclip există deja în listă.");
         return;
      }

      const cleanTitle =
         String(newTitle || "").trim() ||
         `Material video ${String(videos.length + 1).padStart(2, "0")}`;

      setAddBusy(true);
      setAlerts([]);
      try {
         await createRecommendedVideo({
            title: cleanTitle,
            youtubeUrl: watchUrl(youtubeId),
         });
         if (!aliveRef.current) return;
         setNewLink("");
         setNewTitle("");
         setView("list");
         pushAlert("success", "Videoclip creat.");
         await refresh();
      } catch (e) {
         if (aliveRef.current) {
            pushAlert("error", e?.message || "Nu s-a putut crea videoclipul.");
         }
      } finally {
         if (aliveRef.current) setAddBusy(false);
      }
   };

   const handleSaveEdit = async () => {
      if (!selectedVideo || editBusy) return;

      const youtubeId = parseYoutubeId(editLink);
      if (!youtubeId) {
         pushAlert(
            "error",
            "Link YouTube invalid. Corectează linkul înainte de salvare.",
         );
         return;
      }

      const duplicate = videos.find(
         (video) =>
            video.youtubeId === youtubeId &&
            String(video.id) !== String(selectedVideo.id),
      );
      if (duplicate) {
         pushAlert("error", "Există deja alt videoclip cu acest link YouTube.");
         return;
      }

      const title =
         String(editTitle || "").trim() ||
         selectedVideo.title ||
         "Material video";

      setEditBusy(true);
      setAlerts([]);
      try {
         await updateRecommendedVideo(selectedVideo.id, {
            title,
            youtubeUrl: watchUrl(youtubeId),
         });
         if (!aliveRef.current) return;
         pushAlert("success", "Videoclip actualizat.");
         await refresh();
      } catch (e) {
         if (aliveRef.current) {
            pushAlert(
               "error",
               e?.message || "Nu s-a putut actualiza videoclipul.",
            );
         }
      } finally {
         if (aliveRef.current) setEditBusy(false);
      }
   };

   const handleDelete = async () => {
      if (!selectedVideo || deleteBusy) return;

      setDeleteBusy(true);
      setAlerts([]);
      try {
         await deleteRecommendedVideo(selectedVideo.id);
         if (!aliveRef.current) return;
         pushAlert("success", "Videoclip șters.");
         setView("list");
         setSelectedVideoId("");
         await refresh();
      } catch (e) {
         if (aliveRef.current) {
            pushAlert(
               "error",
               e?.message ||
                  "Nu s-a putut șterge (poate a fost deja trimis la o lecție).",
            );
         }
      } finally {
         if (aliveRef.current) setDeleteBusy(false);
      }
   };

   const selectedThumbUrl = selectedVideo
      ? `https://img.youtube.com/vi/${selectedVideo.youtubeId}/hqdefault.jpg`
      : "";

   return (
      <div className="studentVideoLibraryPopup">
         <AlertPills messages={alerts} onDismiss={dismissLastAlert} />

         <div className="studentVideoLibraryPopup__header">
            <h3 className="studentVideoLibraryPopup__title">Videoclipuri</h3>
         </div>

         {view !== "edit" ? (
            <div className="studentVideoLibraryPopup__toolbar">
               {view === "list" ? (
                  <div className="studentVideoLibraryPopup__toolbarRow">
                     <input
                        type="text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder="Caută videoclip"
                        className="studentVideoLibraryPopup__search"
                        autoComplete="off"
                     />

                     <button
                        type="button"
                        className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--success"
                        onClick={handleOpenAdd}
                     >
                        Adaugă
                     </button>
                  </div>
               ) : (
                  <div className="studentVideoLibraryPopup__toolbarRow">
                     <button
                        type="button"
                        className="studentVideoLibraryPopup__button"
                        onClick={handleOpenList}
                     >
                        Înapoi
                     </button>
                  </div>
               )}
            </div>
         ) : (
            <div className="studentVideoLibraryPopup__toolbar studentVideoLibraryPopup__toolbar--edit">
               <button
                  type="button"
                  className="studentVideoLibraryPopup__button"
                  onClick={handleOpenList}
               >
                  Înapoi
               </button>
               <button
                  type="button"
                  className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--success"
                  onClick={handleOpenAdd}
               >
                  Adaugă unul nou
               </button>
            </div>
         )}

         <div className="studentVideoLibraryPopup__content">
            {view === "edit" && (
               <>
                  <section className="studentVideoLibraryPopup__preview">
                     {!selectedVideo ? (
                        <div className="studentVideoLibraryPopup__emptyState">
                           <p className="studentVideoLibraryPopup__empty">
                              Videoclipul selectat nu mai există.
                           </p>
                        </div>
                     ) : (
                        <>
                           <div
                              className="studentVideoLibraryPopup__thumb studentVideoLibraryPopup__thumb--large"
                              style={{
                                 backgroundImage: `url(${selectedThumbUrl})`,
                              }}
                           >
                              <h4 className="studentVideoLibraryPopup__cardTitle">
                                 {selectedVideo.title}
                              </h4>
                           </div>
                        </>
                     )}
                  </section>

                  <section className="studentVideoLibraryPopup__panel">
                     {!selectedVideo ? (
                        <div className="studentVideoLibraryPopup__emptyState">
                           <p className="studentVideoLibraryPopup__empty">
                              Videoclipul nu mai există.
                           </p>
                        </div>
                     ) : (
                        <div className="studentVideoLibraryPopup__form">
                           {selectedVideo.sendCount != null ? (
                              <p className="studentVideoLibraryPopup__meta">
                                 Trimis la {selectedVideo.sendCount} lecție(i)
                              </p>
                           ) : null}

                           <label className="studentVideoLibraryPopup__inputWrap">
                              <span className="studentVideoLibraryPopup__label">
                                 Titlu
                              </span>
                              <input
                                 type="text"
                                 value={editTitle}
                                 onChange={(e) => setEditTitle(e.target.value)}
                                 placeholder="Titlu videoclip"
                                 className="studentVideoLibraryPopup__input"
                                 autoComplete="off"
                              />
                           </label>

                           <label className="studentVideoLibraryPopup__inputWrap">
                              <span className="studentVideoLibraryPopup__label">
                                 Link
                              </span>
                              <input
                                 type="text"
                                 value={editLink}
                                 onChange={(e) => setEditLink(e.target.value)}
                                 placeholder="https://www.youtube.com/watch?v=..."
                                 className="studentVideoLibraryPopup__input"
                                 autoComplete="off"
                              />
                           </label>

                           <div className="studentVideoLibraryPopup__actions">
                              <button
                                 type="button"
                                 className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--danger"
                                 onClick={handleDelete}
                                 disabled={deleteBusy || editBusy}
                              >
                                 {deleteBusy ? "Se șterge..." : "Șterge"}
                              </button>
                              <button
                                 type="button"
                                 className="studentVideoLibraryPopup__button"
                                 onClick={() => handleOpenVideo(selectedVideo)}
                              >
                                 Deschide
                              </button>
                              <button
                                 type="button"
                                 className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--primary"
                                 onClick={handleSaveEdit}
                                 disabled={editBusy}
                              >
                                 {editBusy ? "Se salvează..." : "Salvează"}
                              </button>
                           </div>
                        </div>
                     )}
                  </section>
               </>
            )}

            {view === "list" && (
               <section className="studentVideoLibraryPopup__list">
                  {loading ? (
                     <div className="studentVideoLibraryPopup__emptyState">
                        <p className="studentVideoLibraryPopup__empty">
                           Se încarcă...
                        </p>
                     </div>
                  ) : !filteredVideos.length ? (
                     <div className="studentVideoLibraryPopup__emptyState">
                        <p className="studentVideoLibraryPopup__empty">
                           Nu există videoclipuri pentru filtrul curent.
                        </p>
                     </div>
                  ) : (
                     <div className="studentVideoLibraryPopup__grid">
                        {filteredVideos.map((video) => (
                           <article
                              key={video.id}
                              className="studentVideoLibraryPopup__card"
                           >
                              <div
                                 className="studentVideoLibraryPopup__thumb"
                                 style={{
                                    backgroundImage: `url(https://img.youtube.com/vi/${video.youtubeId}/mqdefault.jpg)`,
                                 }}
                              >
                                 <p
                                    className="studentVideoLibraryPopup__cardTitle"
                                    title={video.title}
                                 >
                                    {video.title}
                                 </p>
                              </div>

                              <div className="studentVideoLibraryPopup__actions">
                                 <button
                                    type="button"
                                    className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--ghost"
                                    onClick={() => handleOpenVideo(video)}
                                 >
                                    Vezi
                                 </button>
                                 <button
                                    type="button"
                                    className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--primary"
                                    onClick={() => handleOpenEdit(video.id)}
                                 >
                                    Edit
                                 </button>
                              </div>
                           </article>
                        ))}
                     </div>
                  )}
               </section>
            )}

            {view === "add" && (
               <section className="studentVideoLibraryPopup__panel">
                  <form
                     className="studentVideoLibraryPopup__form"
                     onSubmit={handleAddVideo}
                  >
                     <label className="studentVideoLibraryPopup__inputWrap">
                        <span className="studentVideoLibraryPopup__label">
                           Link
                        </span>
                        <input
                           type="text"
                           value={newLink}
                           onChange={(e) => setNewLink(e.target.value)}
                           placeholder="https://www.youtube.com/watch?v=..."
                           className="studentVideoLibraryPopup__input"
                           autoComplete="off"
                        />
                     </label>

                     <label className="studentVideoLibraryPopup__inputWrap">
                        <span className="studentVideoLibraryPopup__label">
                           Titlu
                        </span>
                        <input
                           type="text"
                           value={newTitle}
                           onChange={(e) => setNewTitle(e.target.value)}
                           placeholder="Titlu opțional"
                           className="studentVideoLibraryPopup__input"
                           autoComplete="off"
                        />
                     </label>

                     <div className="studentVideoLibraryPopup__actions">
                        <button
                           type="submit"
                           className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--success"
                           disabled={addBusy}
                        >
                           {addBusy ? "Se adaugă..." : "Adaugă videoclip"}
                        </button>
                     </div>
                  </form>
               </section>
            )}
         </div>
      </div>
   );
}
